import getClient, { esRespuestaAjena, esMantenimiento } from "./get-client";

import { borrarSesionLocal, establecerSesion } from "./session-token";
import { purgarDictado } from "./dictado-buffer";
import { purgarDrafts } from "./turno-draft-buffer";
import { purgarFirmasConfirmadas } from "./censo-firmas-confirmadas";
// Cubre R8, R9 — .claude/specs/1751-copia-ia-paciente/ (importarlo aquí hace que la purga corra al arrancar la app)
import { purgarCopiasIaLocales } from "./copia-ia-local";
const client = getClient();

// Timeout propio del flujo de autenticación. NO va en get-client.js: ese mismo
// cliente sirve las llamadas al LLM (generar turno, informes), que tardan
// minutos y se romperían con un timeout global. Sin esto, un firewall que
// descarta paquetes en silencio deja el login colgado para siempre y el médico
// ni siquiera ve un error.
// Timeout del POST de login. 45s cubre el peor arranque frío medido (32.9s) con
// margen: uwsgi recicla el worker cada 10.000 requests y el que cae en el worker
// respawneado paga el import de Django + la conexión a RDS. Queda además bajo el
// idle_timeout de 60s del ALB, así que un cuelgue real llega como 504 — un error
// CON respuesta HTTP, que cae en la rama "server" ("no es tu clave") en vez de
// en un timeout ciego del navegador.
const AUTH_TIMEOUT_MS = 45000;

// El GET de CSRF es OPCIONAL (ver getCsrfToken) y corre ANTES del POST, así que
// su espera se SUMA a la del POST. Con los dos en 45s el peor caso que veía el
// médico era de 90s con el botón bloqueado — medido, no estimado. Un request
// que el login no necesita no puede costar eso.
//
// 6s y NO 8s a propósito: SLOW_LOGIN_MS (el aviso "esto está tardando" + la
// sonda de red) dispara a los 8s. Si los dos coincidieran, el corte del CSRF,
// el aviso y el auto-open competirían en el mismo instante. Separarlos deja el
// orden siempre determinista: primero se abandona el CSRF, después avisa.
const CSRF_TIMEOUT_MS = 6000;

// Mensajes de error del login. La regla es que el médico pueda distinguir
// "me equivoqué de clave" de "mi institución bloquea el servidor": antes todo
// caía en "Usuario o contraseña incorrectos" y una usuaria de hospital perdió
// horas cambiando su contraseña mientras el firewall bloqueaba el backend.
const AUTH_ERROR_MESSAGES = {
  network:
    "No se pudo conectar con el servidor. Puede ser la red o el firewall de tu institución.",
  timeout:
    "El servidor no respondió a tiempo. Puede ser tu conexión o la red de tu institución.",
  credentials: "Usuario o contraseña incorrectos",
  throttled: "Demasiados intentos. Espera un minuto antes de volver a probar.",
  server:
    "El servidor tuvo un problema, no es tu clave. Intenta de nuevo en unos minutos.",
  // Tu clave estaba bien y el servidor emitió el token, pero el navegador no
  // deja guardarlo (Safari privado / QuotaExceededError al escribir).
  storage:
    "Tu clave es correcta, pero el navegador no deja guardar la sesión. Habilita las cookies y el almacenamiento para este sitio, o prueba fuera del modo incógnito.",
  // El 403 de un appliance corporativo. Antes caía en `unknown` y le decía al
  // médico "escríbele a soporte y menciona este código": Hugo Inzulza leyó eso
  // y se cambió la contraseña tres minutos antes de avisarnos (10-sep-2026).
  bloqueo:
    "La red de tu institución está bloqueando SmartDx. No es tu clave. Puedes entrar desde otra red, o pedirle a tu equipo de informática que habilite smardx.com.",
};

// Traduce un error de axios a { kind, message }. `kind` lo usa la pantalla de
// login para abrir sola el autodiagnóstico cuando el problema es de red.
const describeAuthError = (error) => {
  // Timeout: axios corta con ECONNABORTED (o ETIMEDOUT) y sin response.
  if (error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT") {
    return { kind: "timeout", message: AUTH_ERROR_MESSAGES.timeout };
  }
  // Sin response = el request salió del navegador pero nunca volvió nada:
  // firewall, proxy corporativo, DNS, CORS bloqueado o backend caído. Mismo
  // criterio que ya usa el interceptor de get-client.js para mandar a Sentry.
  if (!error?.response) {
    return { kind: "network", message: AUTH_ERROR_MESSAGES.network };
  }
  // Hay respuesta, pero no salió de nuestro backend: es el filtro de la red
  // contestando con su página de bloqueo. Va al cajón "network" a propósito,
  // para que el autodiagnóstico se abra solo (con `unknown` no se abría).
  if (esRespuestaAjena(error)) {
    return { kind: "network", message: AUTH_ERROR_MESSAGES.bloqueo };
  }
  // El 502/503 de un deploy (nginx/ALB/CloudFront, HTML), legible porque llegó por la
  // vía same-origin. Va ANTES de la rama 5xx: el mensaje es el mismo ("no es tu
  // clave") pero `kind` deja que la pantalla sepa que el banner global de
  // mantenimiento está explicando el problema. 500, 504 y un 503 en JSON NO pasan acá.
  // Cubre R1, R2 — .claude/specs/1757-login-mantenimiento/ (v2)
  if (esMantenimiento(error)) {
    return { kind: "maintenance", message: AUTH_ERROR_MESSAGES.server };
  }
  const status = error.response.status;
  // Credenciales malas: DRF (ObtainAuthToken) responde 400, no 401. Cubrimos
  // los dos para no depender de ese detalle.
  if (status === 400 || status === 401) {
    return { kind: "credentials", message: AUTH_ERROR_MESSAGES.credentials };
  }
  if (status === 429) {
    return { kind: "throttled", message: AUTH_ERROR_MESSAGES.throttled };
  }
  if (status >= 500) {
    return { kind: "server", message: AUTH_ERROR_MESSAGES.server };
  }
  // Status que no sabemos interpretar (403, 404, 409...). NO lo mandamos a
  // revisar la red: en estos casos la conexión funciona y el diagnóstico diría
  // "OK", dejando al médico sin salida. Va directo a soporte con el código.
  return {
    kind: "unknown",
    message: `No pudimos iniciar sesión (error ${status}). No es tu contraseña: escríbele a soporte y menciona este código.`,
  };
};

// Log de un fallo de auth SIN el error de axios: el AxiosError lleva `config.data`
// (el cuerpo del POST con el correo y la CLAVE en claro) y `config.headers`.
// Vale para login, register y el CSRF. Cubre R14 — .claude/specs/1757-login-mantenimiento/
const logFallo = (que, error) => {
  console.log(que, { code: error?.code, status: error?.response?.status });
};

const getCsrfToken = async (options = {}) => {
  try {
    const response = await client.get("/api/user/csrf/", {
      withCredentials: true,
      timeout: options.timeout || AUTH_TIMEOUT_MS,
    });
    return response.data.csrfToken;
  } catch (error) {
    // Se traga a propósito: el CSRF es opcional para /api/user/token/ y el
    // login sigue igual sin él. Lo que NO puede pasar es abortar acá.
    logFallo("Error fetching CSRF token", error);
    return null;
  }
};

// Aplana los errores por campo que devuelve DRF ({"phone": ["..."], "email":
// ["..."]}) en un mensaje legible. #1401, R17: el médico tiene que ver POR
// QUÉ falló (celular mal formado, correo repetido, clave débil), no un
// genérico — y el error tiene que salir del backend, no inventarse acá.
const describeRegisterError = (error) => {
  if (!error?.response) {
    return AUTH_ERROR_MESSAGES.network;
  }
  const data = error.response.data;
  if (data && typeof data === "object") {
    const campos = Object.entries(data).filter(([key]) => key !== "detail");
    const mensajes = campos.flatMap(([, value]) =>
      Array.isArray(value) ? value : [value],
    );
    if (mensajes.length) return mensajes.join(" ");
    if (data.detail) return String(data.detail);
  }
  if (error.response.status === 429) return AUTH_ERROR_MESSAGES.throttled;
  if (error.response.status >= 500) return AUTH_ERROR_MESSAGES.server;
  return "No pudimos crear tu cuenta. Intenta de nuevo.";
};

// #1401: manda el payload explícito que el backend espera (`phone` ya
// normalizado por el caller, `accept_tos` de la casilla) — nunca reenvía
// `acceptTerms` ni otras keys sueltas del formData del front.
//
// R17: NUNCA se traga el error. Antes esta función atrapaba TODO y el
// caller encadenaba `.then()` sin importar si el registro había fallado, así
// que un correo repetido terminaba igual llamando a `login()`. Ahora
// `setErrorMessage` deja el mensaje visible Y se re-lanza el error para que
// quien llama (`submitRegistration`) pueda distinguir éxito de fracaso con
// `.then()/.catch()` y NUNCA intente loguear tras un registro rechazado.
// Sin `alert()` de ningún tipo (ni de éxito ni de error).
const register = async (event, formData, setFormData, setErrorMessage) => {
  event.preventDefault(); // Prevent default form submission
  const csrfToken = await getCsrfToken();

  // #1453 R6: sin `username`; el backend lo deriva de la parte local del correo.
  const payload = {
    email: formData.email,
    password: formData.password,
    phone: formData.phone,
    // #1590: país del médico; decide la regla del celular en el backend.
    country: formData.country || "CL",
    accept_tos: true,
    // #1683 v2 (R58): sin casilla de WhatsApp. El permiso lo da el backend al registrar la aceptación
    // de los Términos (`accept_tos`), con fecha, versión y origen `terminos`.
  };

  try {
    await client.post("/api/user/create/", payload, {
      headers: csrfToken ? { "X-CSRFToken": csrfToken } : {},
      withCredentials: true,
    });

    setFormData({
      email: "",
      password: "",
      phone: "",
      country: "CL",
    });
  } catch (error) {
    logFallo("Error de registro", error);
    setErrorMessage(describeRegisterError(error));
    throw error;
  }
};

const login = async (event, formData, setFormData, setErrorMessage, options = {}) => {
  event.preventDefault();

  // Si el CSRF falla NO se aborta el login: se sigue al POST igual.
  //
  // El endpoint /api/user/token/ es un APIView de DRF (CSRF-exempt) y responde
  // 200 sin ninguna cabecera CSRF, así que este GET es opcional. Abortar acá
  // reintroducía el bug original por otra puerta: con el arranque frío del
  // backend (uwsgi recicla el worker cada 10.000 requests y el siguiente paga
  // el import de Django + la conexión a RDS — medido en 32.9s) el que se comía
  // la espera era este GET, el login moría sin siquiera intentar el POST, y
  // para cuando el panel sondeaba /health/ el backend ya estaba caliente: le
  // decía "Conexión OK, revisá tu contraseña" a alguien con la clave correcta.
  // Siguiendo al POST, ese caso pasa de "login roto" a "login lento pero
  // exitoso", porque el backend ya se calentó con el intento del CSRF.
  const csrfToken = await getCsrfToken({ timeout: CSRF_TIMEOUT_MS });

  try {
    const response = await client.post(
      "/api/user/token/",
      {
        email: formData.email,
        password: formData.password,
      },
      {
        headers: csrfToken ? { "X-CSRFToken": csrfToken } : {},
        withCredentials: true,
        timeout: AUTH_TIMEOUT_MS,
      },
    );
    // El try acotado va SOLO alrededor del guardado, que es lo único que puede
    // fallar con el token ya emitido. Un flag amplio le atribuiría al navegador
    // cualquier excepción posterior.
    //
    // Alcance real: cubre que setItem TIRE (Safari privado, QuotaExceededError).
    // NO cubre que el navegador bloquee el ACCESO al almacenamiento (Chrome con
    // los datos de sitio bloqueados por política de grupo), porque ahí tira el
    // getter de localStorage y la app ni siquiera llega a renderizar el login
    // — get-client.js y UserContext.js lo leen sin protección al arrancar.
    // Eso es pre-existente y se arregla aparte.
    // `establecerSesion` guarda el token Y arranca el reloj de inactividad
    // (limpiarMarca + marcarActividad forzada): en un puesto compartido el que
    // entra no puede pagar la ausencia del anterior ni quedarse con su
    // exención. Devuelve false si el navegador no deja escribir.
    if (!establecerSesion(response.data["token"])) {
      setErrorMessage(AUTH_ERROR_MESSAGES.storage);
      return { kind: "storage", message: AUTH_ERROR_MESSAGES.storage };
    }

    // Recién con la sesión guardada se limpia el formulario: si el guardado
    // falla, el médico conserva lo que escribió para reintentar.
    setFormData({
      email: "",
      password: "",
    });

    if (options.redirectTo) {
      window.location.href = options.redirectTo;
    } else {
      window.location.reload();
    }
    return { kind: "ok", message: "" };
  } catch (error) {
    logFallo("Error de login", error);
    const failure = describeAuthError(error);
    setErrorMessage(failure.message);
    return failure;
  }
};

// La limpieza de `logout()` SIN la navegación. La usa también "eliminar mi cuenta" (#1683 R55):
// tras borrarse, la persona aterriza en la pantalla pública `cuenta-eliminada`, no en el login, y
// los borradores del Turno y el diario del dictado (texto clínico en este aparato) tienen que irse
// igual que al cerrar sesión. Una sola lista de qué se borra: si se agrega algo acá, vale para las dos.
// Cubre R55 — .claude/specs/1683-derechos-usuario/
const limpiarSesionLocal = ({ conservarMarcaPagar = false } = {}) => {
  // El diario del dictado guarda texto clínico en este aparato para que no se
  // pierda entre que se transcribe y llega a una casilla. Al cerrar sesión deja
  // de tener dueño, y en un computador de turno el siguiente que entra es otra
  // persona: se borra acá y no se espera a que venza solo. Ley 21.719.
  try {
    purgarDictado();
  } catch (_) {
    // Cerrar sesión nunca se bloquea por la limpieza.
  }
  // Mismo riesgo, mismo motivo (#1255, 3ª pasada del revisor): el write-ahead
  // de celdas/bloc del Turno quedaba vivo después de cerrar sesión y un médico
  // distinto que entrara en el MISMO computador se lo encontraba — y en el caso
  // del bloc, se le llegaba a persistir en su propia pestaña. El aislamiento
  // por `authToken` en `turno-draft-buffer.js` ya corta eso desde el primer
  // keystroke; esto es la segunda capa, para no dejar nada dando vueltas.
  try {
    purgarDrafts();
  } catch (_) {
    // Cerrar sesión nunca se bloquea por la limpieza.
  }
  // #1809 (revisor v11, P1): la marca del link `?pagar=1` (SuscripcionGate) no se usó con esta cuenta;
  // la siguiente persona que entre en esta pestaña no la hereda. EXCEPTO en el cierre por inactividad al
  // cargar (revisor v12, F1): esa marca la acaba de escribir ESTA carga (el médico abrió el link con la
  // sesión vencida), y borrarla lo dejaba en «Al día» sin dónde pagar tras volver a entrar.
  if (!conservarMarcaPagar) {
    try {
      window.sessionStorage.removeItem("smartdx_abrir_aviso_pago");
    } catch (_) {
      // Cerrar sesión nunca se bloquea por la limpieza.
    }
  }
  // #807 Fase B (F3, revisor 23-sep-2026): el gate de confirmación del mapeo
  // inferido (`censo-firmas-confirmadas.js`) tiene dueño por `authToken`
  // desde el primer click, pero se purga acá igual — misma defensa en
  // profundidad que arriba, para que no quede nada dando vueltas si el token
  // se reutiliza. Sin PHI adentro (son firmas estructurales, no texto
  // clínico), pero el patrón es el mismo por consistencia.
  try {
    purgarFirmasConfirmadas();
  } catch (_) {
    // Cerrar sesión nunca se bloquea por la limpieza.
  }
  // #1751 R9: la "versión IA" que guardaba el panel de Evolución en el navegador
  // (`turno_llm_baseline.*`) ya no se escribe, pero puede quedar la del bundle anterior.
  try {
    purgarCopiasIaLocales();
  } catch (_) {
    // Cerrar sesión nunca se bloquea por la limpieza.
  }
  // La cola de telemetría NO se purga acá, y es deliberado.
  //
  // La primera versión sí lo hacía, porque su clave llevaba el `authToken`
  // textual y cerrar sesión dejaba una credencial viva en el disco de un
  // computador de turno. Pero purgar atacaba el síntoma: se llevaba puesto el
  // `idle_logout` que `idle-telemetria` emite un instante antes, o sea
  // justamente la métrica de "el cierre automático, ¿funciona?", y solo cuando
  // el flush no había llegado — la red de hospital bloqueada que este trabajo
  // vino a cubrir.
  //
  // La causa se arregló de raíz: el dueño del buffer es ahora una HUELLA no
  // reversible del token (`activity-log.js`), así que no hay credencial que
  // robar, y el `meta` se sanea antes de tocar el disco. Lo que queda es dato
  // PHI-safe de una sesión que terminó: se entrega cuando se pueda, y si nadie
  // vuelve con ese token lo barre el TTL de 24 h.
  //
  // `purgarActivityQueue()` sigue exportada para quien la necesite.
  borrarSesionLocal();
};

const logout = (opciones) => {
  limpiarSesionLocal(opciones);
  window.location.href = "/authentication";
};

export { register, login, logout, limpiarSesionLocal, describeAuthError, AUTH_ERROR_MESSAGES, getCsrfToken };
